import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import ContentPasteGoRoundedIcon from "@mui/icons-material/ContentPasteGoRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";
import { getMyStores, getMyStoreCategories } from "../../api/storeService";
import { getCategories, getCategoryAttributes } from "../../api/categoryService";
import { removeImageBackground } from "../../api/imageService";
import { translateToArabicAndHebrew } from "../../api/translationService";
import {
  getMyProducts,
  getDeletedProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  restoreProduct,
  deleteProductPermanently,
  uploadProductImage,
  addProductImageFromUrl,
  removeProductImageBackground,
  replaceProductImage,
  deleteProductImage,
  setMainProductImage,
  uploadProductVideo,
  deleteProductVideo,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
  uploadVariantImage,
  addVariantImageFromUrl,
  removeVariantImageBackground,
  deleteVariantImage,
  getAuthenticityOptions,
} from "../../api/productService";
import { EmptyState } from "../Admin/analyticsShared";
import { formatCurrency } from "../Admin/constants";

// Numeric values must match the backend's ProductDiscountType enum (Models/Enums.cs).
const DISCOUNT_TYPE_NONE = 0;
const DISCOUNT_TYPE_FIXED = 1;
const DISCOUNT_TYPE_PERCENTAGE = 2;

// Matches the backend's AttributeInputType enum.
const ATTRIBUTE_INPUT_DROPDOWN = 1;
const ATTRIBUTE_INPUT_NUMBER = 3;

// Matches the backend's ProductVideoPlacement enum.
const VIDEO_PLACEMENT_VIDEO_FIRST = 1;
const VIDEO_PLACEMENT_MAIN_THEN_VIDEO = 2;
const VIDEO_PLACEMENT_PHOTOS_FIRST = 3;
const VIDEO_PLACEMENT_OPTIONS = [
  { value: VIDEO_PLACEMENT_VIDEO_FIRST, labelKey: "boProducts:videoPlacementVideoFirst" },
  { value: VIDEO_PLACEMENT_MAIN_THEN_VIDEO, labelKey: "boProducts:videoPlacementMainThenVideo" },
  { value: VIDEO_PLACEMENT_PHOTOS_FIRST, labelKey: "boProducts:videoPlacementPhotosFirst" },
];

const MAX_IMAGES = 8;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const MAX_VIDEO_SIZE_BYTES = 75 * 1024 * 1024;

const emptyVariantForm = {
  variantName: "",
  price: "",
  stockQuantity: "0",
  sku: "",
  discountType: DISCOUNT_TYPE_NONE,
  discountPrice: "",
  discountPercent: "",
  attributeValues: {},
  imageFile: null,
  imageUrl: null,
  imagePreviewUrl: null,
};

// Builds the CreateProductVariantRequest/UpdateProductVariantRequest `attributeValues` list.
// Variant-generating attributes are always dropdowns, so the value is just an option id.
function buildVariantAttributeValuesPayload(variantAttrs, optionIdByAttributeId) {
  return variantAttrs
    .map((attr) => {
      const optionId = optionIdByAttributeId[attr.attributeId];
      return optionId != null ? { attributeId: attr.attributeId, optionId } : null;
    })
    .filter(Boolean);
}

// Products can only be assigned to a leaf category (no subcategories) that the
// store has already linked via its store-categories - mirrors the backend's
// ValidateProductCategoryAsync check.
function buildCategoryOptions(categories, storeCategories) {
  const parentIds = new Set();
  const byId = new Map();
  categories.forEach((c) => {
    byId.set(c.categoryId, c);
    if (c.parentCategoryId != null) parentIds.add(c.parentCategoryId);
  });

  const pathFor = (categoryId) => {
    const parts = [];
    let current = byId.get(categoryId);
    while (current) {
      parts.unshift(current.name);
      current = current.parentCategoryId != null ? byId.get(current.parentCategoryId) : null;
    }
    return parts.join(" › ");
  };

  return storeCategories
    .filter((sc) => !parentIds.has(sc.categoryId))
    .map((sc) => ({ categoryId: sc.categoryId, name: pathFor(sc.categoryId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Reads image bytes off the OS clipboard as Files - used for both the explicit
// "Paste image" button (navigator.clipboard.read(), needs a user-gesture) and
// the global Ctrl+V handler below (clipboardData.items from the paste event).
async function readImageFilesFromClipboard() {
  const clipboardItems = await navigator.clipboard.read();
  const files = [];
  for (const item of clipboardItems) {
    const imageType = item.types.find((type) => type.startsWith("image/"));
    if (!imageType) continue;
    // eslint-disable-next-line no-await-in-loop
    const blob = await item.getType(imageType);
    const ext = imageType.split("/")[1] || "png";
    files.push(new File([blob], `pasted-${Date.now()}.${ext}`, { type: imageType }));
  }
  return files;
}

function imageFilesFromClipboardEvent(e) {
  const items = e.clipboardData?.items;
  if (!items) return [];
  const files = [];
  for (const item of items) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

function resolveDiscountFields(discountType, discountPriceText, discountPercentText, price, t) {
  if (discountType === DISCOUNT_TYPE_FIXED) {
    const parsed = parseFloat(discountPriceText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= price) {
      return { error: t("boProducts:discountPriceError") };
    }
    return { discountType, discountPrice: parsed, discountPercent: null };
  }
  if (discountType === DISCOUNT_TYPE_PERCENTAGE) {
    const parsed = parseFloat(discountPercentText);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed >= 100) {
      return { error: t("boProducts:discountPercentError") };
    }
    return { discountType, discountPrice: null, discountPercent: parsed };
  }
  return { discountType: DISCOUNT_TYPE_NONE, discountPrice: null, discountPercent: null };
}

// Single-select chip row for a dropdown attribute's options (e.g. Size: S/M/L, or
// Color: Black/White/...) - used when building a variant's attribute combination.
function AttributeOptionChips({ options, selectedOptionId, onSelect }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {options.map((option) => (
        <Chip
          key={option.optionId}
          label={option.value}
          size="small"
          color={selectedOptionId === option.optionId ? "primary" : "default"}
          variant={selectedOptionId === option.optionId ? "filled" : "outlined"}
          onClick={() => onSelect(selectedOptionId === option.optionId ? null : option.optionId)}
        />
      ))}
    </Stack>
  );
}

// Small dialog for pasting a direct image URL instead of uploading a file - shared
// between the main product photos and a variant's photo.
function AddImageByUrlDialog({ open, onClose, onAdd, busy, error }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open) setUrl("");
  }, [open]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("boProducts:addFromLinkTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t("boProducts:addFromLinkLabel")}
            placeholder="https://example.com/photo.jpg"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
            fullWidth
            disabled={busy}
          />
          <Typography variant="caption" color="text.secondary">
            {t("boProducts:addFromLinkHint")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:cancel")}
        </Button>
        <Button variant="contained" onClick={() => onAdd(url.trim())} disabled={busy || !url.trim()}>
          {busy ? t("boShared:saving") : t("common:add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Products() {
  const { t } = useTranslation(["boProducts", "boSidebar", "boShared", "common", "boStores"]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [loadingStores, setLoadingStores] = useState(true);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyProductId, setBusyProductId] = useState(null);

  const selectedStore = useMemo(() => stores.find((s) => s.storeId === storeId) || null, [stores, storeId]);

  useEffect(() => {
    let active = true;
    getMyStores()
      .then((data) => {
        if (!active) return;
        setStores(data);
        if (data.length > 0) setStoreId(data[0].storeId);
      })
      .catch(() => {
        if (active) setError(t("boStores:loadError"));
      })
      .finally(() => {
        if (active) setLoadingStores(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reloadProducts() {
    if (!storeId) return;
    setLoadingProducts(true);
    setError("");
    const request = showDeleted ? getDeletedProducts(storeId) : getMyProducts(storeId);
    request
      .then(setProducts)
      .catch(() => setError(t("boProducts:loadError")))
      .finally(() => setLoadingProducts(false));
  }

  useEffect(() => {
    reloadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, showDeleted]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term ? products.filter((p) => p.name.toLowerCase().includes(term)) : products;
    return [...list].sort((a, b) => (a.categoryName || "").localeCompare(b.categoryName || "") || a.name.localeCompare(b.name));
  }, [products, search]);

  function openCreateDialog() {
    setEditingProduct(null);
    setDialogOpen(true);
  }

  function openEditDialog(product) {
    setEditingProduct(product);
    setDialogOpen(true);
  }

  function handleSaved(message) {
    setDialogOpen(false);
    setEditingProduct(null);
    setNotice(message);
    reloadProducts();
  }

  async function handleDelete() {
    const product = deleteTarget;
    setBusyProductId(product.productId);
    try {
      await deleteProduct(product.productId);
      setDeleteTarget(null);
      setNotice(t("boProducts:productDeleted", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:deleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleRestore(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await restoreProduct(product.productId);
      setNotice(t("boProducts:productRestored", { name: product.name }));
      reloadProducts();
    } catch {
      setError(t("boProducts:restoreError"));
    } finally {
      setBusyProductId(null);
    }
  }

  async function handlePermanentDelete(product) {
    setBusyProductId(product.productId);
    setError("");
    try {
      await deleteProductPermanently(product.productId);
      setNotice(t("boProducts:productPermanentlyDeleted", { name: product.name }));
      reloadProducts();
    } catch (err) {
      setError(err.response?.data || t("boProducts:permanentDeleteError"));
    } finally {
      setBusyProductId(null);
    }
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <Inventory2RoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{t("boSidebar:products")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:subtitle")}
            </Typography>
          </Box>
        </Stack>

        {!showDeleted && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateDialog} disabled={!storeId}>
            {t("boProducts:addProduct")}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : t("boShared:somethingWentWrong")}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2.5, alignItems: { xs: "stretch", sm: "center" } }}>
        {stores.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t("boShared:storeFallback")}</InputLabel>
            <Select label={t("boShared:storeFallback")} value={storeId ?? ""} onChange={(e) => setStoreId(e.target.value)}>
              {stores.map((s) => (
                <MenuItem key={s.storeId} value={s.storeId}>
                  {s.storeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          placeholder={t("boProducts:searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260, flexGrow: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Button
          variant={showDeleted ? "contained" : "outlined"}
          color={showDeleted ? "secondary" : "inherit"}
          startIcon={showDeleted ? <Inventory2RoundedIcon /> : <DeleteOutlineRoundedIcon />}
          onClick={() => setShowDeleted((prev) => !prev)}
        >
          {showDeleted ? t("boProducts:backToProducts") : t("boProducts:deletedProducts")}
        </Button>
      </Stack>

      {loadingStores || loadingProducts ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rounded" height={260} />
            </Grid>
          ))}
        </Grid>
      ) : !storeId ? (
        <EmptyState icon={Inventory2RoundedIcon} text={t("boProducts:noStores")} />
      ) : filteredProducts.length === 0 ? (
        <EmptyState
          icon={showDeleted ? DeleteOutlineRoundedIcon : Inventory2RoundedIcon}
          text={showDeleted ? t("boProducts:noDeletedProducts") : t("boProducts:noProducts")}
        />
      ) : (
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.productId}>
              <ProductCard
                product={product}
                busy={busyProductId === product.productId}
                showDeleted={showDeleted}
                onEdit={() => openEditDialog(product)}
                onDelete={() => setDeleteTarget(product)}
                onRestore={() => handleRestore(product)}
                onPermanentDelete={() => handlePermanentDelete(product)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {dialogOpen && (
        <ProductFormDialog
          store={selectedStore}
          product={editingProduct}
          onClose={() => {
            setDialogOpen(false);
            setEditingProduct(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "error.main", width: 36, height: 36 }}>
                <WarningAmberRoundedIcon fontSize="small" />
              </Avatar>
              <span>{t("boProducts:deleteConfirmTitle")}</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t("boProducts:deleteConfirmBody", { name: deleteTarget.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>{t("common:cancel")}</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              {t("common:delete")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function ProductCard({ product, busy, showDeleted, onEdit, onDelete, onRestore, onPermanentDelete }) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const mainImage = product.images?.find((i) => i.isMain)?.imageUrl || product.images?.[0]?.imageUrl || null;

  return (
    <Card
      variant="outlined"
      sx={{ height: "100%", display: "flex", flexDirection: "column", opacity: busy ? 0.6 : 1, position: "relative" }}
    >
      <Box
        sx={{
          aspectRatio: "1 / 1",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {mainImage ? (
          <Box component="img" src={mainImage} alt={product.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Inventory2RoundedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        )}
      </Box>

      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
          {product.categoryName && <Chip size="small" label={product.categoryName} variant="outlined" />}
          {product.authenticityValue && (
            <Chip size="small" label={product.authenticityValue} color="primary" variant="outlined" />
          )}
          {!product.isActive && !showDeleted && <Chip size="small" label={t("boShared:inactive")} color="default" />}
          {product.approvalStatus === 2 && !showDeleted && (
            <Chip size="small" color="warning" icon={<HourglassTopRoundedIcon />} label={t("boProducts:underReview")} />
          )}
          {product.approvalStatus === 3 && !showDeleted && (
            <Tooltip title={product.approvalRejectionReason || ""}>
              <Chip size="small" color="error" label={t("boProducts:rejected")} />
            </Tooltip>
          )}
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} noWrap title={product.name}>
          {product.name}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5, mb: 0.5 }}>
          {product.discountPrice != null ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(product.price)}
              </Typography>
              <Typography variant="subtitle2" color="error.main" fontWeight={700}>
                {formatCurrency(product.discountPrice)}
              </Typography>
            </>
          ) : (
            <Typography variant="subtitle2" fontWeight={700}>
              {formatCurrency(product.price)}
            </Typography>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {product.stockQuantity > 0 ? t("boProducts:inStockCount", { count: product.stockQuantity }) : t("common:outOfStock")}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ my: 1.25 }} />

        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {showDeleted ? (
            <>
              <Tooltip title={t("boProducts:restore")}>
                <IconButton size="small" onClick={onRestore} disabled={busy}>
                  <RestoreFromTrashRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("boProducts:deletePermanently")}>
                <IconButton size="small" color="error" onClick={onPermanentDelete} disabled={busy}>
                  <DeleteForeverRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={t("common:edit")}>
                <IconButton size="small" onClick={onEdit} disabled={busy}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("common:delete")}>
                <IconButton size="small" onClick={onDelete} disabled={busy}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
}

// One titled card in the product form (Basic information, Media, Pricing, ...).
function FormSection({ icon: Icon, title, subtitle, action, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: children ? 2 : 0 }}>
        {Icon && (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {children}
    </Paper>
  );
}

const MEDIA_TILE_SIZE = 104;

const mediaTileSx = {
  position: "relative",
  width: MEDIA_TILE_SIZE,
  height: MEDIA_TILE_SIZE,
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const mediaTileBusySx = {
  position: "absolute",
  inset: 0,
  bgcolor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
};

// Always-visible action strip along the bottom of a photo tile (works on touch, unlike hover menus).
const mediaTileActionsSx = {
  position: "absolute",
  insetInline: 0,
  bottom: 0,
  zIndex: 1,
  display: "flex",
  justifyContent: "center",
  gap: 0.25,
  py: 0.25,
  bgcolor: "rgba(0,0,0,0.55)",
  "& .MuiIconButton-root": { color: "#fff", p: 0.4, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } },
};

const mainPhotoBadgeSx = {
  position: "absolute",
  top: 4,
  insetInlineStart: 4,
  zIndex: 1,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  borderRadius: 1,
  px: 0.75,
  lineHeight: 1.6,
  fontSize: 10,
  fontWeight: 700,
};

const variantRowSx = {
  p: 1.25,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  transition: "border-color .15s",
  "&:hover": { borderColor: "primary.light" },
};

const variantThumbSx = {
  position: "relative",
  width: 48,
  height: 48,
  borderRadius: 1.5,
  overflow: "hidden",
  flexShrink: 0,
  bgcolor: "background.default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid",
  borderColor: "divider",
};

// Also used by the admin Product Approvals page to edit a store's product before publishing it.
// `secondaryAction` ({ label, color, onAfterSave }) adds a second submit button that saves the
// form first and then runs onAfterSave (e.g. approve) before closing.
// `showTranslations` shows the Arabic/Hebrew name + description fields. For now only admins
// translate products (from Product Approvals); store owners don't see or send these fields.
// `reviewer` hides the store-owner review wording without adding a second button - the admin
// Store Products page uses it for products that are already live.
export function ProductFormDialog({
  store,
  product,
  onClose,
  onSaved,
  secondaryAction,
  showTranslations = false,
  reviewer = false,
}) {
  const { t } = useTranslation(["boProducts", "boShared", "common"]);
  const isEditing = Boolean(product);
  const supportsWholesale = store ? store.type === 2 || store.type === 3 : false;
  // Review wording is for the store owner only - the admin approvals page reuses this dialog
  // (with secondaryAction) and is the reviewer itself.
  const showReviewFlow = !secondaryAction && !reviewer;
  const storeRequiresReview = showReviewFlow && Boolean(store?.requiresProductApproval);
  const isPendingReview = showReviewFlow && isEditing && product.approvalStatus === 2;
  const isRejected = showReviewFlow && isEditing && product.approvalStatus === 3;

  const [currentProductId, setCurrentProductId] = useState(product?.productId ?? null);
  const [images, setImages] = useState(product?.images ?? []);
  // Photos/video/variants picked before the product itself is created (no productId
  // to attach them to yet) - uploaded automatically right after the create call succeeds.
  const [pendingImages, setPendingImages] = useState([]);

  const [video, setVideo] = useState(product?.videoUrl ? { videoUrl: product.videoUrl } : null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [videoPlacement, setVideoPlacement] = useState(product?.videoPlacement ?? VIDEO_PLACEMENT_VIDEO_FIRST);

  const [variants, setVariants] = useState(product?.variants ?? []);
  const [pendingVariants, setPendingVariants] = useState([]);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editingPendingVariantIndex, setEditingPendingVariantIndex] = useState(null);
  const [variantForm, setVariantForm] = useState(emptyVariantForm);
  const [savingVariant, setSavingVariant] = useState(false);
  const [busyVariantId, setBusyVariantId] = useState(null);
  const [variantImageLinkDialogOpen, setVariantImageLinkDialogOpen] = useState(false);
  const [processingVariantFormBackground, setProcessingVariantFormBackground] = useState(false);

  const [categories, setCategories] = useState([]);
  const [storeCategories, setStoreCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryId, setCategoryId] = useState(product?.categoryId ?? null);

  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributeValues, setAttributeValues] = useState(() => {
    const map = {};
    (product?.attributeValues || []).forEach((av) => {
      map[av.attributeId] = { optionId: av.optionId ?? null, textValue: av.textValue ?? "" };
    });
    return map;
  });

  const [name, setName] = useState(product?.name ?? "");
  const [nameArabic, setNameArabic] = useState(product?.nameArabic ?? "");
  const [nameHebrew, setNameHebrew] = useState(product?.nameHebrew ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [descriptionArabic, setDescriptionArabic] = useState(product?.descriptionArabic ?? "");
  const [descriptionHebrew, setDescriptionHebrew] = useState(product?.descriptionHebrew ?? "");
  // "name" | "description" | "all" while the Arabic/Hebrew translation is being fetched.
  const [translatingField, setTranslatingField] = useState(null);
  const [draggingPhotos, setDraggingPhotos] = useState(false);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [price,setPrice] = useState(product ? String(product.price) : "");
  const [wholesalePrice, setWholesalePrice] = useState(product?.wholesalePrice != null ? String(product.wholesalePrice) : "");
  const [discountType, setDiscountType] = useState(product?.discountType ?? DISCOUNT_TYPE_NONE);
  const [discountPrice, setDiscountPrice] = useState(product?.discountPrice != null ? String(product.discountPrice) : "");
  const [discountPercent, setDiscountPercent] = useState(product?.discountPercent != null ? String(product.discountPercent) : "");
  const [stockQuantity, setStockQuantity] = useState(product ? String(product.stockQuantity) : "0");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [authenticityOptions, setAuthenticityOptions] = useState([]);
  const [authenticityOptionId, setAuthenticityOptionId] = useState(product?.authenticityOptionId ?? null);
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [requiresSpecialDelivery, setRequiresSpecialDelivery] = useState(product?.requiresSpecialDelivery ?? false);

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [busyImageId, setBusyImageId] = useState(null);
  const [processingPendingImageIndex, setProcessingPendingImageIndex] = useState(null);
  const [imageLinkDialogOpen, setImageLinkDialogOpen] = useState(false);
  const [addingImageFromLink, setAddingImageFromLink] = useState(false);
  const [imageLinkError, setImageLinkError] = useState("");
  const [formError, setFormError] = useState("");

  const categoryOptions = useMemo(() => buildCategoryOptions(categories, storeCategories), [categories, storeCategories]);
  const descriptiveAttributes = useMemo(
    () => categoryAttributes.filter((a) => !a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );
  const variantAttributes = useMemo(
    () => categoryAttributes.filter((a) => a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder),
    [categoryAttributes],
  );

  useEffect(() => {
    let active = true;
    getAuthenticityOptions()
      .then((options) => {
        if (active) setAuthenticityOptions(options);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!store) return;
    let active = true;
    setLoadingCategories(true);
    Promise.all([getCategories(), getMyStoreCategories(store.storeId)])
      .then(([allCategories, myStoreCategories]) => {
        if (!active) return;
        setCategories(allCategories.filter((c) => c.isActive));
        setStoreCategories(myStoreCategories);
      })
      .catch(() => {
        if (active) setFormError(t("boProducts:loadCategoriesError"));
      })
      .finally(() => {
        if (active) setLoadingCategories(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  useEffect(() => {
    if (categoryOptions.length === 0 || isEditing) return;
    const isValid = categoryOptions.some((c) => c.categoryId === categoryId);
    if (!isValid) setCategoryId(categoryOptions[0].categoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions]);

  useEffect(() => {
    if (!categoryId) {
      setCategoryAttributes([]);
      return;
    }
    let active = true;
    setLoadingAttributes(true);
    getCategoryAttributes(categoryId)
      .then((data) => {
        if (active) setCategoryAttributes(data);
      })
      .catch(() => {
        if (active) setCategoryAttributes([]);
      })
      .finally(() => {
        if (active) setLoadingAttributes(false);
      });
    return () => {
      active = false;
    };
  }, [categoryId]);

  // Lets Ctrl+V paste an image copied from another site (e.g. with the image
  // copier extension) straight into whichever photo area is active - the
  // variant form's photo if it's open, otherwise the main product photos.
  // Only intercepts the paste when clipboard actually holds image data, so
  // normal text pasting into the other fields is untouched.
  useEffect(() => {
    function onPaste(e) {
      const files = imageFilesFromClipboardEvent(e);
      if (files.length === 0) return;
      e.preventDefault();
      setFormError("");
      if (showVariantForm) {
        addVariantImageFile(files[0]);
      } else {
        addPhotoFiles(files);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  });

  function setAttributeOptionValue(attributeId, optionId) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId, textValue: "" } }));
  }

  function setAttributeTextValue(attributeId, textValue) {
    setAttributeValues((prev) => ({ ...prev, [attributeId]: { optionId: null, textValue } }));
  }

  function buildAttributeValuesPayload() {
    return descriptiveAttributes
      .map((attr) => {
        const value = attributeValues[attr.attributeId];
        if (!value) return null;
        if (attr.inputType === ATTRIBUTE_INPUT_DROPDOWN) {
          return value.optionId != null ? { attributeId: attr.attributeId, optionId: value.optionId } : null;
        }
        const trimmed = (value.textValue || "").trim();
        return trimmed ? { attributeId: attr.attributeId, textValue: trimmed } : null;
      })
      .filter(Boolean);
  }

  // Fills the Arabic + Hebrew fields from the English name and/or description (overwriting them).
  // `field` is "name", "description", or "all".
  async function handleTranslate(field) {
    const fields = (field === "all" ? ["name", "description"] : [field]).filter((f) =>
      (f === "name" ? name : description).trim(),
    );
    if (fields.length === 0) return;

    setTranslatingField(field);
    setFormError("");
    try {
      await Promise.all(
        fields.map(async (f) => {
          const { arabic, hebrew } = await translateToArabicAndHebrew(f === "name" ? name : description);
          if (f === "name") {
            setNameArabic(arabic);
            setNameHebrew(hebrew);
          } else {
            setDescriptionArabic(arabic);
            setDescriptionHebrew(hebrew);
          }
        }),
      );
    } catch {
      setFormError(t("boProducts:translateFailed"));
    } finally {
      setTranslatingField(null);
    }
  }

  // Small translate icon shown inside the English name / description inputs.
  function translateAdornment(field, source) {
    if (!showTranslations) return undefined;
    return (
      <InputAdornment position="end" sx={field === "description" ? { alignSelf: "flex-start", mt: 1.5 } : undefined}>
        <Tooltip title={t("boProducts:translateTooltip")}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleTranslate(field)}
              disabled={!source.trim() || translatingField !== null}
            >
              {translatingField === field || translatingField === "all" ? (
                <CircularProgress size={18} />
              ) : (
                <TranslateRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    );
  }

  // Ctrl/Cmd + S saves the product from anywhere in the dialog.
  function handleDialogKeyDown(e) {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      if (!saving && name.trim() && categoryId) handleSubmit();
    }
  }

  function handlePhotoDrop(e) {
    e.preventDefault();
    setDraggingPhotos(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length > 0) addPhotoFiles(files);
  }

  async function handleSubmit(afterSave) {
    // The variant editor persists to the server on its own "Save Variant"
    // button, not this one - if it's still open, its edits haven't been
    // pushed yet. Block here instead of silently discarding them.
    if (showVariantForm) {
      setFormError(t("boProducts:unsavedVariantError"));
      return;
    }

    const trimmedName = name.trim();
    const parsedPrice = parseFloat(price);

    if (!categoryId) {
      setFormError(t("boProducts:chooseCategoryError"));
      return;
    }
    if (!trimmedName) {
      setFormError(t("boProducts:enterNameError"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidPriceError"));
      return;
    }

    let parsedWholesalePrice = null;
    if (supportsWholesale && wholesalePrice.trim() !== "") {
      parsedWholesalePrice = parseFloat(wholesalePrice);
      if (Number.isNaN(parsedWholesalePrice) || parsedWholesalePrice < 0) {
        setFormError(t("boProducts:invalidWholesalePriceError"));
        return;
      }
    }

    const discountResult = resolveDiscountFields(discountType, discountPrice, discountPercent, parsedPrice, t);
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    const attributeValuesPayload = buildAttributeValuesPayload();
    const missingRequired = descriptiveAttributes.filter(
      (a) => a.isRequired && !attributeValuesPayload.some((v) => v.attributeId === a.attributeId),
    );
    if (missingRequired.length > 0) {
      setFormError(t("boProducts:missingAttributes", { list: missingRequired.map((a) => a.attributeName).join(", ") }));
      return;
    }

    setFormError("");
    setSaving(true);

    const payload = {
      categoryId,
      name: trimmedName,
      description: description.trim() || null,
      // Omitted fields keep their stored value on the server; "" clears a translation.
      ...(showTranslations && {
        nameArabic: nameArabic.trim(),
        nameHebrew: nameHebrew.trim(),
        descriptionArabic: descriptionArabic.trim(),
        descriptionHebrew: descriptionHebrew.trim(),
      }),
      price: parsedPrice,
      wholesalePrice: parsedWholesalePrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(stockQuantity, 10) || 0,
      sku: sku.trim() || null,
      authenticityOptionId,
      requiresSpecialDelivery,
      attributeValues: attributeValuesPayload,
    };

    try {
      if (isEditing) {
        const updated = await updateProduct(currentProductId, { ...payload, isActive, videoPlacement });
        if (afterSave) await afterSave();
        onSaved(
          isRejected && updated?.approvalStatus === 2
            ? t("boProducts:productResubmitted", { name: trimmedName })
            : t("boProducts:productUpdated", { name: trimmedName }),
        );
      } else if (currentProductId) {
        // Product was already created in this dialog session (e.g. photos were added); this
        // submit just updates the rest of the fields.
        await updateProduct(currentProductId, { ...payload, isActive: true, videoPlacement });
        onSaved(t("boProducts:productUpdated", { name: trimmedName }));
      } else {
        const created = await createProduct({ ...payload, storeId: store.storeId });
        setCurrentProductId(created.productId);
        setImages(created.images || []);

        if (pendingImages.length > 0) {
          await uploadPendingImages(created.productId);
        }
        if (pendingVideo) {
          await uploadPendingVideo(created.productId);
        }
        if (pendingVariants.length > 0) {
          await uploadPendingVariants(created.productId);
        }
        onSaved(
          created.approvalStatus === 2
            ? t("boProducts:productCreatedPendingReview")
            : t("boProducts:productCreatedNotice"),
        );
      }
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:saveError"));
    } finally {
      setSaving(false);
    }
  }

  function validateImageFile(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setFormError(t("boShared:invalidImageType"));
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFormError(t("boProducts:imageTooLarge"));
      return false;
    }
    return true;
  }

  async function addPhotoFiles(files) {
    const remaining = MAX_IMAGES - (images.length + pendingImages.length);
    const validFiles = files.slice(0, remaining).filter(validateImageFile);
    if (validFiles.length === 0) return;

    if (!currentProductId) {
      setFormError("");
      setPendingImages((prev) => [
        ...prev,
        ...validFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
      ]);
      return;
    }

    setFormError("");
    setUploadingImage(true);
    try {
      for (const file of validFiles) {
        // eslint-disable-next-line no-await-in-loop
        const updated = await uploadProductImage(currentProductId, file);
        setImages(updated.images);
      }
    } catch {
      setFormError(t("boProducts:uploadPhotoError"));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleAddImage(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await addPhotoFiles(files);
  }

  // Reads an image directly from the OS clipboard - lets photos copied with an
  // image-copying browser extension (or any "Copy image" action) be added here
  // with one click, no save-then-upload round trip needed.
  async function handlePasteImageFromClipboard() {
    if (images.length + pendingImages.length >= MAX_IMAGES) return;
    if (!navigator.clipboard?.read) {
      setFormError(t("boProducts:clipboardPasteUnsupported"));
      return;
    }
    setFormError("");
    try {
      const files = await readImageFilesFromClipboard();
      if (files.length === 0) {
        setFormError(t("boProducts:noImageInClipboard"));
        return;
      }
      await addPhotoFiles(files);
    } catch {
      setFormError(t("boProducts:clipboardPasteError"));
    }
  }

  function removePendingImage(index) {
    setPendingImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadPendingImages(targetProductId) {
    for (const pending of pendingImages) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const updated = pending.file
          ? await uploadProductImage(targetProductId, pending.file)
          : await addProductImageFromUrl(targetProductId, pending.url);
        setImages(updated.images);
      } catch {
        setFormError(t("boProducts:someImagesFailedUpload"));
      }
    }
    setPendingImages([]);
  }

  async function handleAddImageFromUrl(url) {
    if (!url) return;
    setImageLinkError("");

    if (!currentProductId) {
      const remaining = MAX_IMAGES - (images.length + pendingImages.length);
      if (remaining <= 0) return;
      setPendingImages((prev) => [...prev, { url, previewUrl: url }]);
      setImageLinkDialogOpen(false);
      return;
    }

    setAddingImageFromLink(true);
    try {
      const updated = await addProductImageFromUrl(currentProductId, url);
      setImages(updated.images);
      setImageLinkDialogOpen(false);
    } catch (err) {
      const data = err.response?.data;
      setImageLinkError(typeof data === "string" && data ? data : t("boProducts:addFromLinkError"));
    } finally {
      setAddingImageFromLink(false);
    }
  }

  async function handleRemoveImageBackground(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await removeProductImageBackground(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleRemovePendingImageBackground(index) {
    const pending = pendingImages[index];
    if (!pending?.file) return;

    setFormError("");
    setProcessingPendingImageIndex(index);
    try {
      const blob = await removeImageBackground(pending.file);
      const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
      setPendingImages((prev) =>
        prev.map((p, i) => (i === index ? { file: resultFile, previewUrl: URL.createObjectURL(resultFile) } : p)),
      );
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setProcessingPendingImageIndex(null);
    }
  }

  async function handleReplaceImage(imageId, e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateImageFile(file)) return;

    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await replaceProductImage(currentProductId, imageId, file);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:replacePhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleDeleteImage(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await deleteProductImage(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:removePhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleSetMainImage(imageId) {
    setFormError("");
    setBusyImageId(imageId);
    try {
      const updated = await setMainProductImage(currentProductId, imageId);
      setImages(updated.images);
    } catch {
      setFormError(t("boProducts:setMainPhotoError"));
    } finally {
      setBusyImageId(null);
    }
  }

  async function handleAddVideo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      setFormError(t("boProducts:invalidVideoType"));
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      setFormError(t("boProducts:videoTooLarge"));
      return;
    }

    setFormError("");

    if (!currentProductId) {
      setPendingVideo({ file, previewUrl: URL.createObjectURL(file) });
      return;
    }

    setUploadingVideo(true);
    try {
      const updated = await uploadProductVideo(currentProductId, file);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:uploadVideoError"));
    } finally {
      setUploadingVideo(false);
    }
  }

  async function uploadPendingVideo(targetProductId) {
    if (!pendingVideo) return;
    try {
      const updated = await uploadProductVideo(targetProductId, pendingVideo.file);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:uploadVideoError"));
    }
    setPendingVideo(null);
  }

  async function handleDeleteVideo() {
    setFormError("");
    setDeletingVideo(true);
    try {
      const updated = await deleteProductVideo(currentProductId);
      setVideo(updated.videoUrl ? { videoUrl: updated.videoUrl } : null);
    } catch {
      setFormError(t("boProducts:removeVideoError"));
    } finally {
      setDeletingVideo(false);
    }
  }

  function openAddVariant() {
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
    setShowVariantForm(true);
  }

  function openEditVariant(variant) {
    setEditingVariantId(variant.variantId);
    setEditingPendingVariantIndex(null);
    const attributeValuesMap = {};
    (variant.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: variant.variantName,
      price: String(variant.price),
      stockQuantity: String(variant.stockQuantity),
      sku: variant.sku || "",
      discountType: variant.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice: variant.discountPrice != null ? String(variant.discountPrice) : "",
      discountPercent: variant.discountPercent != null ? String(variant.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: null,
      imageUrl: null,
      imagePreviewUrl: variant.imageUrl || null,
    });
    setShowVariantForm(true);
  }

  // A variant staged locally before the product itself has been created (no
  // productId yet to attach it to) - mirrors pendingImages/pendingVideo.
  function openEditPendingVariant(index) {
    const pending = pendingVariants[index];
    setEditingVariantId(null);
    setEditingPendingVariantIndex(index);
    const attributeValuesMap = {};
    (pending.attributeValues || []).forEach((av) => {
      attributeValuesMap[av.attributeId] = av.optionId;
    });
    setVariantForm({
      variantName: pending.variantName || "",
      price: String(pending.price),
      stockQuantity: String(pending.stockQuantity),
      sku: pending.sku || "",
      discountType: pending.discountType ?? DISCOUNT_TYPE_NONE,
      discountPrice: pending.discountPrice != null ? String(pending.discountPrice) : "",
      discountPercent: pending.discountPercent != null ? String(pending.discountPercent) : "",
      attributeValues: attributeValuesMap,
      imageFile: pending.imageFile,
      imageUrl: pending.imageUrl,
      imagePreviewUrl: pending.imagePreviewUrl,
    });
    setShowVariantForm(true);
  }

  function removePendingVariant(index) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    setPendingVariants((prev) => prev.filter((_, i) => i !== index));
  }

  function cancelVariantForm() {
    setShowVariantForm(false);
    setEditingVariantId(null);
    setEditingPendingVariantIndex(null);
    setVariantForm(emptyVariantForm);
  }

  function setVariantAttributeOption(attributeId, optionId) {
    setVariantForm((prev) => ({ ...prev, attributeValues: { ...prev.attributeValues, [attributeId]: optionId } }));
  }

  function addVariantImageFile(file) {
    if (!file || !validateImageFile(file)) return;

    if (editingVariantId) {
      handleUploadVariantImage(editingVariantId, file);
      return;
    }
    setVariantForm((prev) => ({ ...prev, imageFile: file, imageUrl: null, imagePreviewUrl: URL.createObjectURL(file) }));
  }

  function handleVariantImageFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    addVariantImageFile(file);
  }

  // Same idea as handlePasteImageFromClipboard, but for the variant photo picker.
  async function handlePasteVariantImageFromClipboard() {
    if (!navigator.clipboard?.read) {
      setFormError(t("boProducts:clipboardPasteUnsupported"));
      return;
    }
    setFormError("");
    try {
      const files = await readImageFilesFromClipboard();
      if (files.length === 0) {
        setFormError(t("boProducts:noImageInClipboard"));
        return;
      }
      addVariantImageFile(files[0]);
    } catch {
      setFormError(t("boProducts:clipboardPasteError"));
    }
  }

  function handleVariantImageFromUrl(url) {
    if (!url) return;
    if (editingVariantId) {
      handleAddVariantImageFromUrl(editingVariantId, url);
      setVariantImageLinkDialogOpen(false);
      return;
    }
    setVariantForm((prev) => ({ ...prev, imageFile: null, imageUrl: url, imagePreviewUrl: url }));
    setVariantImageLinkDialogOpen(false);
  }

  async function handleUploadVariantImage(variantId, file) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await uploadVariantImage(currentProductId, variantId, file);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
    } catch {
      setFormError(t("boProducts:uploadVariantPhotoError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleAddVariantImageFromUrl(variantId, url) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await addVariantImageFromUrl(currentProductId, variantId, url);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
    } catch {
      setFormError(t("boProducts:addFromLinkError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleRemoveVariantImageBackground(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await removeVariantImageBackground(currentProductId, variantId);
      setVariants(updated.variants);
      const savedVariant = updated.variants.find((v) => v.variantId === variantId);
      if (editingVariantId === variantId) {
        setVariantForm((prev) => ({ ...prev, imagePreviewUrl: savedVariant?.imageUrl ?? prev.imagePreviewUrl }));
      }
    } catch {
      setFormError(t("boProducts:removeBackgroundError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function handleRemoveVariantFormImageBackground() {
    setFormError("");
    if (variantForm.imageFile) {
      setProcessingVariantFormBackground(true);
      try {
        const blob = await removeImageBackground(variantForm.imageFile);
        const resultFile = new File([blob], `bg-removed-${Date.now()}.png`, { type: "image/png" });
        setVariantForm((prev) => ({ ...prev, imageFile: resultFile, imageUrl: null, imagePreviewUrl: URL.createObjectURL(resultFile) }));
      } catch {
        setFormError(t("boProducts:removeBackgroundError"));
      } finally {
        setProcessingVariantFormBackground(false);
      }
      return;
    }
    if (editingVariantId) {
      await handleRemoveVariantImageBackground(editingVariantId);
    }
  }

  async function handleDeleteVariantImage(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await deleteVariantImage(currentProductId, variantId);
      setVariants(updated.variants);
    } catch {
      setFormError(t("boProducts:removeVariantPhotoError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  async function saveVariant() {
    const trimmedName = variantForm.variantName.trim();
    const parsedPrice = parseFloat(variantForm.price);
    const variantAttributeValuesPayload = buildVariantAttributeValuesPayload(
      variantAttributes,
      variantForm.attributeValues,
    );

    if (variantAttributes.length > 0 && variantAttributeValuesPayload.length !== variantAttributes.length) {
      setFormError(
        t("boProducts:missingVariantAttributes", {
          attributes: variantAttributes.map((a) => a.attributeName).join(", "),
        }),
      );
      return;
    }
    if (!trimmedName && variantAttributeValuesPayload.length === 0) {
      setFormError(t("boProducts:missingVariantName"));
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setFormError(t("boProducts:invalidVariantPrice"));
      return;
    }

    const discountResult = resolveDiscountFields(
      variantForm.discountType,
      variantForm.discountPrice,
      variantForm.discountPercent,
      parsedPrice,
      t,
    );
    if (discountResult.error) {
      setFormError(discountResult.error);
      return;
    }

    setFormError("");

    const payload = {
      variantName: trimmedName || null,
      price: parsedPrice,
      discountType: discountResult.discountType,
      discountPrice: discountResult.discountPrice,
      discountPercent: discountResult.discountPercent,
      stockQuantity: parseInt(variantForm.stockQuantity, 10) || 0,
      sku: variantForm.sku.trim() || null,
      attributeValues: variantAttributeValuesPayload,
    };

    // The product doesn't exist yet, so there's no productId to attach a variant
    // to - stage it locally and create it via uploadPendingVariants() right after
    // the product itself is created (same pattern as pendingImages/pendingVideo).
    if (!currentProductId) {
      setPendingVariants((prev) => {
        const entry = {
          ...payload,
          imageFile: variantForm.imageFile,
          imageUrl: variantForm.imageUrl,
          imagePreviewUrl: variantForm.imagePreviewUrl,
        };
        if (editingPendingVariantIndex != null) {
          const copy = [...prev];
          copy[editingPendingVariantIndex] = entry;
          return copy;
        }
        return [...prev, entry];
      });
      cancelVariantForm();
      return;
    }

    setSavingVariant(true);

    try {
      const previousVariantIds = new Set(variants.map((v) => v.variantId));
      const updated = editingVariantId
        ? await updateProductVariant(currentProductId, editingVariantId, { ...payload, isActive: true })
        : await createProductVariant(currentProductId, payload);
      setVariants(updated.variants);

      if (!editingVariantId && (variantForm.imageFile || variantForm.imageUrl)) {
        const createdVariant = updated.variants.find((v) => !previousVariantIds.has(v.variantId));
        if (createdVariant) {
          if (variantForm.imageFile) {
            await handleUploadVariantImage(createdVariant.variantId, variantForm.imageFile);
          } else {
            await handleAddVariantImageFromUrl(createdVariant.variantId, variantForm.imageUrl);
          }
        }
      }

      cancelVariantForm();
    } catch (err) {
      const data = err.response?.data;
      setFormError(typeof data === "string" && data ? data : t("boProducts:saveVariantError"));
    } finally {
      setSavingVariant(false);
    }
  }

  async function uploadPendingVariants(targetProductId) {
    let currentVariants = [];
    for (const pending of pendingVariants) {
      try {
        const previousIds = new Set(currentVariants.map((v) => v.variantId));
        const { imageFile, imageUrl, imagePreviewUrl, ...variantPayload } = pending;
        // eslint-disable-next-line no-await-in-loop
        const updated = await createProductVariant(targetProductId, variantPayload);
        currentVariants = updated.variants;
        setVariants(currentVariants);

        if (imageFile || imageUrl) {
          const createdVariant = currentVariants.find((v) => !previousIds.has(v.variantId));
          if (createdVariant) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const withImage = imageFile
                ? await uploadVariantImage(targetProductId, createdVariant.variantId, imageFile)
                : await addVariantImageFromUrl(targetProductId, createdVariant.variantId, imageUrl);
              currentVariants = withImage.variants;
              setVariants(currentVariants);
            } catch {
              setFormError(t("boProducts:uploadVariantPhotoError"));
            }
          }
        }
      } catch {
        setFormError(t("boProducts:saveVariantError"));
      }
    }
    setPendingVariants([]);
  }

  function confirmDeleteVariant(variantId) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("boProducts:removeVariantConfirm"))) return;
    handleDeleteVariant(variantId);
  }

  async function handleDeleteVariant(variantId) {
    setFormError("");
    setBusyVariantId(variantId);
    try {
      const updated = await deleteProductVariant(currentProductId, variantId);
      setVariants(updated.variants);
    } catch {
      setFormError(t("boProducts:removeVariantError"));
    } finally {
      setBusyVariantId(null);
    }
  }

  const photoCount = images.length + pendingImages.length;
  const canAddPhotos = photoCount < MAX_IMAGES;
  const mainImageUrl = currentProductId
    ? (images.find((img) => img.isMain) ?? images[0])?.imageUrl
    : pendingImages[0]?.previewUrl;

  // Live price preview - mirrors the rules in resolveDiscountFields, but never errors.
  const previewPrice = parseFloat(price);
  const hasValidPrice = !Number.isNaN(previewPrice) && previewPrice >= 0;
  let previewSalePrice = null;
  if (hasValidPrice && discountType === DISCOUNT_TYPE_FIXED) {
    const value = parseFloat(discountPrice);
    if (value > 0 && value < previewPrice) previewSalePrice = value;
  } else if (hasValidPrice && discountType === DISCOUNT_TYPE_PERCENTAGE) {
    const percent = parseFloat(discountPercent);
    if (percent > 0 && percent < 100) previewSalePrice = Math.round(previewPrice * (100 - percent)) / 100;
  }
  const previewDiscountPercent =
    previewSalePrice != null && previewPrice > 0 ? Math.round((1 - previewSalePrice / previewPrice) * 100) : null;
  const isOutOfStock = stockQuantity !== "" && Number(stockQuantity) <= 0;

  // Description translations only count once there's an English description to translate.
  const translationFields = description.trim()
    ? [nameArabic, nameHebrew, descriptionArabic, descriptionHebrew]
    : [nameArabic, nameHebrew];
  const translatedCount = translationFields.filter((v) => v.trim()).length;
  const translationLanguages = [
    {
      code: "ar",
      nativeName: "العربية",
      label: t("boProducts:languageArabic"),
      name: nameArabic,
      setName: setNameArabic,
      nameLabel: t("boProducts:nameArabic"),
      description: descriptionArabic,
      setDescription: setDescriptionArabic,
      descriptionLabel: t("boProducts:descriptionArabic"),
    },
    {
      code: "he",
      nativeName: "עברית",
      label: t("boProducts:languageHebrew"),
      name: nameHebrew,
      setName: setNameHebrew,
      nameLabel: t("boProducts:nameHebrew"),
      description: descriptionHebrew,
      setDescription: setDescriptionHebrew,
      descriptionLabel: t("boProducts:descriptionHebrew"),
    },
  ];

  const isNewUnsaved = !isEditing && !currentProductId;
  const canSubmit = !saving && Boolean(name.trim()) && Boolean(categoryId);
  let submitLabel;
  if (saving) submitLabel = t("boShared:saving");
  else if (!isNewUnsaved) submitLabel = isRejected ? t("boProducts:saveAndResubmit") : t("boProducts:saveChanges");
  else submitLabel = storeRequiresReview ? t("boProducts:sendForReview") : t("boProducts:createProduct");

  // Inputs sitting on a tinted panel keep a solid background so they read as fields.
  const fieldOnTintSx = { "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } };
  const discountToggleSx = { "& .MuiToggleButton-root": { px: 1.75, textTransform: "none", fontWeight: 600 } };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      onKeyDown={handleDialogKeyDown}
      slotProps={{
        paper: {
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            height: fullScreen ? "100%" : "min(92vh, 1100px)",
            bgcolor: "background.default",
            backgroundImage: "none",
          },
        },
      }}
    >
      {/* ---------- Header ---------- */}
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="center"
        sx={{ px: { xs: 2, sm: 3 }, py: 1.75, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Avatar
          variant="rounded"
          src={mainImageUrl}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: "action.hover",
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
            "& img": { objectFit: "contain" },
          }}
        >
          <Inventory2RoundedIcon />
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {isEditing ? name.trim() || product.name : t("boProducts:newProductTitle")}
            </Typography>
            {isEditing && (
              <Stack direction="row" spacing={0.75} sx={{ display: { xs: "none", sm: "flex" }, flexShrink: 0 }}>
                <Chip
                  size="small"
                  label={isActive ? t("boShared:active") : t("boShared:inactive")}
                  color={isActive ? "success" : "default"}
                  variant={isActive ? "filled" : "outlined"}
                />
                {product.approvalStatus === 2 && <Chip size="small" color="warning" label={t("boProducts:underReview")} />}
                {product.approvalStatus === 3 && <Chip size="small" color="error" label={t("boProducts:rejected")} />}
              </Stack>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[isEditing ? t("boProducts:editProduct") : t("boProducts:newProductSubtitle"), store?.name].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Tooltip title={t("common:close")}>
          <span>
            <IconButton onClick={onClose} disabled={saving}>
              <CloseRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {(isPendingReview || isRejected) && (
            <Grid size={12}>
              {isPendingReview && (
                <Alert severity="warning" icon={<HourglassTopRoundedIcon fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2">{t("boProducts:pendingReviewTitle")}</Typography>
                  {t("boProducts:pendingReviewText")}
                </Alert>
              )}
              {isRejected && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2">{t("boProducts:rejectedTitle")}</Typography>
                  {product.approvalRejectionReason && (
                    <Box>{t("boProducts:rejectedReason", { reason: product.approvalRejectionReason })}</Box>
                  )}
                  <Box>{t("boProducts:rejectedHint")}</Box>
                </Alert>
              )}
            </Grid>
          )}

          {/* ---------- Main column ---------- */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              {/* Basic information */}
              <FormSection
                icon={ArticleOutlinedIcon}
                title={t("boProducts:sectionBasicInfo")}
                subtitle={t("boProducts:sectionBasicInfoHint")}
              >
                <Stack spacing={2}>
                  <TextField
                    label={t("boProducts:name")}
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus={!isEditing}
                    slotProps={{ input: { endAdornment: translateAdornment("name", name) } }}
                  />
                  <TextField
                    label={t("boShared:description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={14}
                    slotProps={{ input: { endAdornment: translateAdornment("description", description) } }}
                  />
                </Stack>
              </FormSection>

              {/* Translations */}
              {showTranslations && (
                <FormSection
                  icon={TranslateRoundedIcon}
                  title={t("boProducts:sectionTranslations")}
                  subtitle={t("boProducts:sectionTranslationsHint")}
                  action={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={t("boProducts:translatedCount", { done: translatedCount, total: translationFields.length })}
                        color={translatedCount === translationFields.length ? "success" : "default"}
                        variant={translatedCount === translationFields.length ? "filled" : "outlined"}
                        sx={{ display: { xs: "none", sm: "flex" } }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={() => handleTranslate("all")}
                        disabled={translatingField !== null || (!name.trim() && !description.trim())}
                        startIcon={
                          translatingField === "all" ? <CircularProgress size={14} color="inherit" /> : <TranslateRoundedIcon />
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("boProducts:translateAll")}
                      </Button>
                    </Stack>
                  }
                >
                  <Grid container spacing={2}>
                    {translationLanguages.map((lang) => {
                      const complete = Boolean(lang.name.trim()) && (!description.trim() || Boolean(lang.description.trim()));
                      return (
                        <Grid key={lang.code} size={{ xs: 12, sm: 6 }}>
                          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover", height: "100%" }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {lang.nativeName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {lang.label}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              {complete && <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} />}
                            </Stack>
                            <Stack spacing={1.5}>
                              <TextField
                                label={lang.nameLabel}
                                value={lang.name}
                                onChange={(e) => lang.setName(e.target.value)}
                                size="small"
                                fullWidth
                                slotProps={{ htmlInput: { dir: "rtl", lang: lang.code } }}
                                sx={fieldOnTintSx}
                              />
                              <TextField
                                label={lang.descriptionLabel}
                                value={lang.description}
                                onChange={(e) => lang.setDescription(e.target.value)}
                                size="small"
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={10}
                                slotProps={{ htmlInput: { dir: "rtl", lang: lang.code } }}
                                sx={fieldOnTintSx}
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </FormSection>
              )}

              {/* Media */}
              <FormSection
                icon={PhotoLibraryOutlinedIcon}
                title={t("boProducts:sectionMedia")}
                subtitle={t("boProducts:sectionMediaHint")}
                action={
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${photoCount} / ${MAX_IMAGES}`}
                    color={canAddPhotos ? "default" : "warning"}
                  />
                }
              >
                <Box
                  onDragOver={(e) => {
                    if (!canAddPhotos) return;
                    e.preventDefault();
                    setDraggingPhotos(true);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setDraggingPhotos(false);
                  }}
                  onDrop={canAddPhotos ? handlePhotoDrop : undefined}
                  sx={{
                    p: 1,
                    m: -1,
                    borderRadius: 2.5,
                    border: "2px dashed",
                    borderColor: draggingPhotos ? "primary.main" : "transparent",
                    bgcolor: draggingPhotos ? "action.hover" : "transparent",
                    transition: "all .15s",
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {!currentProductId &&
                      pendingImages.map((pending, index) => (
                        <Box
                          key={index}
                          sx={{ ...mediaTileSx, ...(index === 0 && { border: "2px solid", borderColor: "primary.main" }) }}
                        >
                          <Box component="img" src={pending.previewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                          {index === 0 && <Box sx={mainPhotoBadgeSx}>{t("boProducts:mainPhoto")}</Box>}
                          {processingPendingImageIndex === index && (
                            <Box sx={mediaTileBusySx}>
                              <CircularProgress size={20} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Box sx={mediaTileActionsSx}>
                            {pending.file && (
                              <Tooltip title={t("boProducts:removeBackground")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemovePendingImageBackground(index)}
                                    disabled={processingPendingImageIndex === index}
                                  >
                                    <AutoFixHighRoundedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={t("common:delete")}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => removePendingImage(index)}
                                  disabled={processingPendingImageIndex === index}
                                >
                                  <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}

                    {currentProductId &&
                      images.map((img) => (
                        <Box
                          key={img.imageId}
                          sx={{ ...mediaTileSx, ...(img.isMain && { border: "2px solid", borderColor: "primary.main" }) }}
                        >
                          <Box component="img" src={img.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                          {img.isMain && <Box sx={mainPhotoBadgeSx}>{t("boProducts:mainPhoto")}</Box>}
                          {busyImageId === img.imageId && (
                            <Box sx={mediaTileBusySx}>
                              <CircularProgress size={20} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Tooltip title={t("boProducts:replacePhoto")}>
                            <Box component="label" sx={{ position: "absolute", inset: 0, cursor: "pointer" }}>
                              <input type="file" accept="image/*" hidden onChange={(e) => handleReplaceImage(img.imageId, e)} />
                            </Box>
                          </Tooltip>
                          <Box sx={mediaTileActionsSx}>
                            <Tooltip title={img.isMain ? t("boProducts:mainPhoto") : t("boProducts:setAsMain")}>
                              <IconButton size="small" onClick={() => !img.isMain && handleSetMainImage(img.imageId)}>
                                {img.isMain ? (
                                  <StarRoundedIcon sx={{ fontSize: 16, color: "#FFC857" }} />
                                ) : (
                                  <StarOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("boProducts:removeBackground")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveImageBackground(img.imageId);
                                }}
                              >
                                <AutoFixHighRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("common:delete")}>
                              <IconButton size="small" onClick={() => handleDeleteImage(img.imageId)}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}

                    {canAddPhotos && (
                      <Box
                        component="label"
                        sx={{
                          ...mediaTileSx,
                          border: "1.5px dashed",
                          borderColor: "divider",
                          bgcolor: "transparent",
                          flexDirection: "column",
                          gap: 0.25,
                          px: 1,
                          textAlign: "center",
                          color: "text.secondary",
                          cursor: uploadingImage ? "default" : "pointer",
                          transition: "all .15s",
                          "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        {uploadingImage ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <CloudUploadOutlinedIcon sx={{ fontSize: 24 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                              {t("boProducts:uploadPhotos")}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.2, color: "text.disabled" }}>
                              {t("boProducts:orDragAndDrop")}
                            </Typography>
                          </>
                        )}
                        <input type="file" accept="image/*" multiple hidden onChange={handleAddImage} disabled={uploadingImage} />
                      </Box>
                    )}
                  </Box>
                </Box>

                {canAddPhotos && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<InsertLinkRoundedIcon />}
                      onClick={() => setImageLinkDialogOpen(true)}
                      sx={{ borderColor: "divider" }}
                    >
                      {t("boProducts:addFromLink")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<ContentPasteGoRoundedIcon />}
                      onClick={handlePasteImageFromClipboard}
                      sx={{ borderColor: "divider" }}
                    >
                      {t("boProducts:pasteImage")}
                    </Button>
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {t("boProducts:pasteImageHint")}
                </Typography>

                <Divider sx={{ my: 2.5 }} />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <VideocamOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t("boProducts:video")}
                  </Typography>
                </Stack>
                {photoCount === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                    {t("boProducts:addPhotoBeforeVideoHint")}
                  </Typography>
                ) : (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                    {video || pendingVideo ? (
                      <Box sx={{ ...mediaTileSx, width: 220, height: 140, bgcolor: "#000" }}>
                        <Box
                          component="video"
                          src={video ? video.videoUrl : pendingVideo.previewUrl}
                          controls
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                        {deletingVideo ? (
                          <Box sx={mediaTileBusySx}>
                            <CircularProgress size={20} sx={{ color: "#fff" }} />
                          </Box>
                        ) : (
                          <Tooltip title={t("common:delete")}>
                            <IconButton
                              size="small"
                              onClick={video ? handleDeleteVideo : () => setPendingVideo(null)}
                              sx={{
                                position: "absolute",
                                top: 4,
                                insetInlineEnd: 4,
                                bgcolor: "rgba(0,0,0,0.55)",
                                color: "#fff",
                                p: 0.5,
                                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                              }}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Box
                        component="label"
                        sx={{
                          ...mediaTileSx,
                          width: 220,
                          border: "1.5px dashed",
                          borderColor: "divider",
                          bgcolor: "transparent",
                          flexDirection: "column",
                          gap: 0.5,
                          color: "text.secondary",
                          cursor: uploadingVideo ? "default" : "pointer",
                          transition: "all .15s",
                          "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        {uploadingVideo ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <VideocamOutlinedIcon sx={{ fontSize: 24 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {t("boProducts:addVideo")}
                            </Typography>
                          </>
                        )}
                        <input type="file" accept="video/mp4,video/quicktime" hidden onChange={handleAddVideo} disabled={uploadingVideo} />
                      </Box>
                    )}

                    {isEditing && video && (
                      <TextField
                        select
                        size="small"
                        label={t("boProducts:videoPlacement")}
                        value={videoPlacement}
                        onChange={(e) => setVideoPlacement(Number(e.target.value))}
                        sx={{ minWidth: 260, flexGrow: 1 }}
                      >
                        {VIDEO_PLACEMENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>
                )}
              </FormSection>

              {/* Pricing & inventory */}
              <FormSection icon={SellOutlinedIcon} title={t("boProducts:sectionPricing")} subtitle={t("boProducts:sectionPricingHint")}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t("common:price")}
                      required
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      fullWidth
                      slotProps={{
                        input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                        htmlInput: { min: 0, step: "0.01" },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t("boProducts:stockQuantity")}
                      type="number"
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      fullWidth
                      helperText={isOutOfStock ? t("boProducts:outOfStock") : undefined}
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      sx={isOutOfStock ? { "& .MuiFormHelperText-root": { color: "warning.main", fontWeight: 600 } } : undefined}
                    />
                  </Grid>
                  {supportsWholesale && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t("boProducts:wholesalePriceOptional")}
                        type="number"
                        value={wholesalePrice}
                        onChange={(e) => setWholesalePrice(e.target.value)}
                        fullWidth
                        slotProps={{
                          input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                          htmlInput: { min: 0, step: "0.01" },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: supportsWholesale ? 6 : 12 }}>
                    <TextField label={t("boProducts:skuOptional")} value={sku} onChange={(e) => setSku(e.target.value)} fullWidth />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                  {t("boShared:discount")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={discountType}
                    onChange={(_, value) => value != null && setDiscountType(value)}
                    sx={discountToggleSx}
                  >
                    <ToggleButton value={DISCOUNT_TYPE_NONE}>{t("boProducts:discountNone")}</ToggleButton>
                    <ToggleButton value={DISCOUNT_TYPE_FIXED}>{t("boProducts:discountFixed")}</ToggleButton>
                    <ToggleButton value={DISCOUNT_TYPE_PERCENTAGE}>{t("boProducts:discountPercentage")}</ToggleButton>
                  </ToggleButtonGroup>
                  {discountType === DISCOUNT_TYPE_FIXED && (
                    <TextField
                      label={t("boProducts:salePrice")}
                      type="number"
                      value={discountPrice}
                      onChange={(e) => setDiscountPrice(e.target.value)}
                      size="small"
                      sx={{ flexGrow: 1 }}
                      slotProps={{
                        input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                        htmlInput: { min: 0, step: "0.01" },
                      }}
                    />
                  )}
                  {discountType === DISCOUNT_TYPE_PERCENTAGE && (
                    <TextField
                      label={t("boProducts:discountPercentLabel")}
                      type="number"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                      size="small"
                      sx={{ flexGrow: 1 }}
                      slotProps={{
                        input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                        htmlInput: { min: 0, max: 100, step: 1 },
                      }}
                    />
                  )}
                </Stack>
                {previewSalePrice != null && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      mt: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                      color: "success.dark",
                    }}
                  >
                    <SellOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                      {t("boProducts:customersPay")} <b>{formatCurrency(previewSalePrice)}</b>{" "}
                      <Box component="span" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                        {formatCurrency(previewPrice)}
                      </Box>{" "}
                      (−{previewDiscountPercent}%)
                    </Typography>
                  </Stack>
                )}
              </FormSection>

              {/* Category attributes */}
              {(loadingAttributes || descriptiveAttributes.length > 0) && (
                <FormSection
                  icon={TuneRoundedIcon}
                  title={t("boProducts:categoryAttributes")}
                  subtitle={t("boProducts:sectionAttributesHint")}
                >
                  {loadingAttributes ? (
                    <Grid container spacing={2}>
                      {[0, 1].map((i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6 }}>
                          <Skeleton variant="rounded" height={40} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {descriptiveAttributes.map((attr) => (
                        <Grid key={attr.attributeId} size={{ xs: 12, sm: 6 }}>
                          {attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? (
                            <FormControl size="small" fullWidth>
                              <InputLabel>
                                {attr.attributeName}
                                {attr.isRequired ? " *" : ""}
                              </InputLabel>
                              <Select
                                label={`${attr.attributeName}${attr.isRequired ? " *" : ""}`}
                                value={attributeValues[attr.attributeId]?.optionId ?? ""}
                                onChange={(e) => setAttributeOptionValue(attr.attributeId, e.target.value)}
                              >
                                {attr.options.map((opt) => (
                                  <MenuItem key={opt.optionId} value={opt.optionId}>
                                    {opt.value}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              label={`${attr.attributeName}${attr.unit ? ` (${attr.unit})` : ""}${attr.isRequired ? " *" : ""}`}
                              type={attr.inputType === ATTRIBUTE_INPUT_NUMBER ? "number" : "text"}
                              value={attributeValues[attr.attributeId]?.textValue ?? ""}
                              onChange={(e) => setAttributeTextValue(attr.attributeId, e.target.value)}
                              size="small"
                              fullWidth
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </FormSection>
              )}

              {/* Variants */}
              <FormSection
                icon={StyleOutlinedIcon}
                title={t("boProducts:variants")}
                subtitle={t("boProducts:variantsHint")}
                action={
                  !showVariantForm && (
                    <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={openAddVariant}>
                      {t("boProducts:addVariant")}
                    </Button>
                  )
                }
              >
                <Stack spacing={1.25}>
                  {variants.length + pendingVariants.length === 0 && !showVariantForm && (
                    <Box
                      sx={{
                        py: 3,
                        px: 2,
                        textAlign: "center",
                        borderRadius: 2,
                        border: "1.5px dashed",
                        borderColor: "divider",
                        color: "text.secondary",
                      }}
                    >
                      <StyleOutlinedIcon sx={{ fontSize: 28, opacity: 0.5 }} />
                      <Typography variant="body2">{t("boProducts:noVariants")}</Typography>
                    </Box>
                  )}

                  {variants.map((v) => (
                    <Stack key={v.variantId} direction="row" spacing={1.5} alignItems="center" sx={variantRowSx}>
                      <Box sx={variantThumbSx}>
                        {busyVariantId === v.variantId ? (
                          <CircularProgress size={16} />
                        ) : v.imageUrl ? (
                          <Box component="img" src={v.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : (
                          <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap title={v.variantName}>
                            {v.variantName}
                          </Typography>
                          {!v.isActive && (
                            <Chip size="small" variant="outlined" label={t("boProducts:variantInactive")} sx={{ height: 20, fontSize: 11 }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {v.discountPrice != null ? (
                            <>
                              <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                                {formatCurrency(v.price)}
                              </Box>
                              <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                                {formatCurrency(v.discountPrice)}
                              </Box>
                            </>
                          ) : (
                            formatCurrency(v.price)
                          )}
                          {" · "}
                          {t("boProducts:inStockCount", { count: v.stockQuantity })}
                          {v.sku ? ` · ${v.sku}` : ""}
                        </Typography>
                      </Box>
                      {v.imageUrl && (
                        <Tooltip title={t("boProducts:removeBackground")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveVariantImageBackground(v.variantId)}
                              disabled={busyVariantId === v.variantId}
                            >
                              <AutoFixHighRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {v.imageUrl && (
                        <Tooltip title={t("boProducts:removeVariantPhoto")}>
                          <span>
                            <IconButton size="small" onClick={() => handleDeleteVariantImage(v.variantId)} disabled={busyVariantId === v.variantId}>
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={t("common:edit")}>
                        <IconButton size="small" onClick={() => openEditVariant(v)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:delete")}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDeleteVariant(v.variantId)}
                            disabled={busyVariantId === v.variantId}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}

                  {pendingVariants.map((pv, index) => (
                    <Stack key={index} direction="row" spacing={1.5} alignItems="center" sx={variantRowSx}>
                      <Box sx={variantThumbSx}>
                        {pv.imagePreviewUrl ? (
                          <Box component="img" src={pv.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        ) : (
                          <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={pv.variantName || t("boProducts:variantName")}>
                          {pv.variantName || t("boProducts:variantName")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pv.discountPrice != null ? (
                            <>
                              <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
                                {formatCurrency(pv.price)}
                              </Box>
                              <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                                {formatCurrency(pv.discountPrice)}
                              </Box>
                            </>
                          ) : (
                            formatCurrency(pv.price)
                          )}
                          {" · "}
                          {t("boProducts:inStockCount", { count: pv.stockQuantity })}
                          {pv.sku ? ` · ${pv.sku}` : ""}
                        </Typography>
                      </Box>
                      <Tooltip title={t("common:edit")}>
                        <IconButton size="small" onClick={() => openEditPendingVariant(index)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common:delete")}>
                        <IconButton size="small" color="error" onClick={() => removePendingVariant(index)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}

                  {showVariantForm && (
                    <Stack
                      spacing={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1.5px solid",
                        borderColor: "primary.main",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                          <Box
                            component="label"
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: 2,
                              border: "1.5px dashed",
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              overflow: "hidden",
                              bgcolor: "background.paper",
                              "&:hover": { borderColor: "primary.main" },
                            }}
                          >
                            {variantForm.imagePreviewUrl ? (
                              <Box component="img" src={variantForm.imagePreviewUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            ) : (
                              <PhotoCameraOutlinedIcon sx={{ fontSize: 22, color: "text.secondary" }} />
                            )}
                            <input type="file" accept="image/*" hidden onChange={handleVariantImageFile} />
                          </Box>
                          {(processingVariantFormBackground || (editingVariantId != null && busyVariantId === editingVariantId)) && (
                            <Box sx={{ ...mediaTileBusySx, borderRadius: 2 }}>
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            </Box>
                          )}
                          <Stack direction="row" spacing={0.25} sx={{ position: "absolute", top: 2, insetInlineEnd: 2 }}>
                            <Tooltip title={t("boProducts:addFromLink")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setVariantImageLinkDialogOpen(true);
                                }}
                                sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                              >
                                <InsertLinkRoundedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("boProducts:pasteImage")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePasteVariantImageFromClipboard();
                                }}
                                sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                              >
                                <ContentPasteGoRoundedIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            {variantForm.imagePreviewUrl && (
                              <Tooltip title={t("boProducts:removeBackground")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleRemoveVariantFormImageBackground();
                                    }}
                                    disabled={processingVariantFormBackground || (editingVariantId != null && busyVariantId === editingVariantId)}
                                    sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "#fff", p: 0.3, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                                  >
                                    <AutoFixHighRoundedIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>

                        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                          {variantAttributes.map((attr) => (
                            <Box key={attr.attributeId}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {attr.attributeName}
                                {attr.unit ? ` (${attr.unit})` : ""} *
                              </Typography>
                              <AttributeOptionChips
                                options={attr.options}
                                selectedOptionId={variantForm.attributeValues[attr.attributeId] ?? null}
                                onSelect={(optionId) => setVariantAttributeOption(attr.attributeId, optionId)}
                              />
                            </Box>
                          ))}
                          <TextField
                            label={variantAttributes.length > 0 ? t("boProducts:variantNameOptionalAuto") : t("boProducts:variantName")}
                            value={variantForm.variantName}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, variantName: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Stack>
                      </Stack>

                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("common:price")}
                            type="number"
                            value={variantForm.price}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, price: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("boProducts:stockQuantity")}
                            type="number"
                            value={variantForm.stockQuantity}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, stockQuantity: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label={t("boProducts:skuOptional")}
                            value={variantForm.sku}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, sku: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={fieldOnTintSx}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          color="primary"
                          value={variantForm.discountType}
                          onChange={(_, value) => value != null && setVariantForm((prev) => ({ ...prev, discountType: value }))}
                          sx={{ ...discountToggleSx, bgcolor: "background.paper" }}
                        >
                          <ToggleButton value={DISCOUNT_TYPE_NONE}>{t("boProducts:discountNone")}</ToggleButton>
                          <ToggleButton value={DISCOUNT_TYPE_FIXED}>{t("boProducts:discountFixed")}</ToggleButton>
                          <ToggleButton value={DISCOUNT_TYPE_PERCENTAGE}>{t("boProducts:discountPercentage")}</ToggleButton>
                        </ToggleButtonGroup>
                        {variantForm.discountType === DISCOUNT_TYPE_FIXED && (
                          <TextField
                            label={t("boProducts:salePrice")}
                            type="number"
                            value={variantForm.discountPrice}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPrice: e.target.value }))}
                            size="small"
                            sx={{ ...fieldOnTintSx, flexGrow: 1 }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> } }}
                          />
                        )}
                        {variantForm.discountType === DISCOUNT_TYPE_PERCENTAGE && (
                          <TextField
                            label={t("boProducts:discountPercentLabel")}
                            type="number"
                            value={variantForm.discountPercent}
                            onChange={(e) => setVariantForm((prev) => ({ ...prev, discountPercent: e.target.value }))}
                            size="small"
                            sx={{ ...fieldOnTintSx, flexGrow: 1 }}
                            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                          />
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button onClick={cancelVariantForm} disabled={savingVariant} color="inherit">
                          {t("common:cancel")}
                        </Button>
                        <Button variant="contained" disableElevation onClick={saveVariant} disabled={savingVariant}>
                          {savingVariant
                            ? t("boShared:saving")
                            : editingVariantId || editingPendingVariantIndex != null
                              ? t("boProducts:saveVariant")
                              : t("boProducts:addVariant")}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </FormSection>
            </Stack>
          </Grid>

          {/* ---------- Side column ---------- */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 1, md: 2 } }}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              {isEditing && (
                <FormSection icon={VisibilityOutlinedIcon} title={t("boProducts:sectionStatus")}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(isActive ? theme.palette.success.main : theme.palette.text.primary, isActive ? 0.08 : 0.04),
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isActive ? "success.dark" : "text.secondary" }}>
                        {isActive ? t("boShared:active") : t("boShared:inactive")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("boProducts:inactiveHint")}
                      </Typography>
                    </Box>
                    <Switch color="success" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  </Stack>
                </FormSection>
              )}

              <FormSection icon={CategoryOutlinedIcon} title={t("boProducts:sectionOrganization")}>
                <Stack spacing={2}>
                  <TextField
                    select
                    required
                    size="small"
                    fullWidth
                    label={t("boProducts:category")}
                    value={categoryId ?? ""}
                    onChange={(e) => setCategoryId(e.target.value)}
                    disabled={loadingCategories || categoryOptions.length === 0}
                    helperText={!loadingCategories && categoryOptions.length === 0 ? t("boProducts:noCategoriesHint") : undefined}
                  >
                    {categoryOptions.map((c) => (
                      <MenuItem key={c.categoryId} value={c.categoryId}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={t("boProducts:authenticity")}
                    value={authenticityOptionId ?? ""}
                    onChange={(e) => setAuthenticityOptionId(e.target.value === "" ? null : Number(e.target.value))}
                    slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
                  >
                    <MenuItem value="">
                      <Typography component="span" color="text.secondary">
                        {t("boProducts:notSpecified")}
                      </Typography>
                    </MenuItem>
                    {authenticityOptions.map((option) => (
                      <MenuItem key={option.authenticityOptionId} value={option.authenticityOptionId}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </FormSection>

              <FormSection icon={LocalShippingOutlinedIcon} title={t("boProducts:sectionShipping")}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t("boProducts:requiresSpecialDelivery")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("boProducts:specialDeliveryHint")}
                    </Typography>
                  </Box>
                  <Switch checked={requiresSpecialDelivery} onChange={(e) => setRequiresSpecialDelivery(e.target.checked)} />
                </Stack>
              </FormSection>

              {/* Live customer preview (desktop only - on phones it would push the form down) */}
              <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", display: { xs: "none", md: "block" } }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <StorefrontOutlinedIcon fontSize="small" color="action" />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}
                  >
                    {t("boProducts:customerPreview")}
                  </Typography>
                </Stack>
                <Box sx={{ p: 2, opacity: isActive ? 1 : 0.55 }}>
                  <Box
                    sx={{
                      position: "relative",
                      aspectRatio: "4 / 3",
                      borderRadius: 2,
                      bgcolor: "background.default",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    {mainImageUrl ? (
                      <Box component="img" src={mainImageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <PhotoCameraOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    )}
                    {previewDiscountPercent != null && (
                      <Chip
                        size="small"
                        color="error"
                        label={`−${previewDiscountPercent}%`}
                        sx={{ position: "absolute", top: 8, insetInlineStart: 8, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      color: name.trim() ? "text.primary" : "text.disabled",
                    }}
                  >
                    {name.trim() || t("boProducts:name")}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: previewSalePrice != null ? "error.main" : "text.primary" }}>
                      {hasValidPrice ? formatCurrency(previewSalePrice ?? previewPrice) : "—"}
                    </Typography>
                    {previewSalePrice != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                        {formatCurrency(previewPrice)}
                      </Typography>
                    )}
                  </Stack>
                  {isOutOfStock && (
                    <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                      {t("boProducts:outOfStock")}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <AddImageByUrlDialog
          open={imageLinkDialogOpen}
          onClose={() => setImageLinkDialogOpen(false)}
          onAdd={handleAddImageFromUrl}
          busy={addingImageFromLink}
          error={imageLinkError}
        />
        <AddImageByUrlDialog
          open={variantImageLinkDialogOpen}
          onClose={() => setVariantImageLinkDialogOpen(false)}
          onAdd={handleVariantImageFromUrl}
          busy={busyVariantId != null && busyVariantId === editingVariantId}
        />
      </DialogContent>

      {/* ---------- Footer (always visible, so errors show next to the button that caused them) ---------- */}
      <DialogActions
        disableSpacing
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          gap: 1.5,
          flexWrap: "wrap",
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          {formError ? (
            <Alert severity="error" onClose={() => setFormError("")} sx={{ py: 0, borderRadius: 2 }}>
              {formError}
            </Alert>
          ) : storeRequiresReview && isNewUnsaved ? (
            <Typography variant="caption" color="text.secondary">
              {t("boProducts:sendForReviewHint")}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ display: { xs: "none", sm: "block" } }}>
              {t("boProducts:saveShortcut")}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, marginInlineStart: "auto" }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant={secondaryAction ? "outlined" : "contained"}
            disableElevation
            disabled={!canSubmit}
            onClick={() => handleSubmit()}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : isRejected || (storeRequiresReview && isNewUnsaved) ? (
                <SendRoundedIcon />
              ) : undefined
            }
            sx={{ px: 2.5 }}
          >
            {submitLabel}
          </Button>
          {secondaryAction && isEditing && (
            <Button
              variant="contained"
              disableElevation
              color={secondaryAction.color || "primary"}
              disabled={!canSubmit}
              onClick={() => handleSubmit(secondaryAction.onAfterSave)}
              sx={{ px: 2.5 }}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
